import type { CSSProperties } from 'react'
import { cx } from '@/lib/cx'

export type BarChartDatum = {
  /** Stable identifier (matches the backend aggregate key), not shown. */
  key: string
  label: string
  value: number
  /** RE-1: optional per-bar fill (e.g. a stage accent or domestic/international
   * hue) — omitted falls back to the single olive fill via `--bar-color`. */
  color?: string
}

type BarChartProps = {
  data: BarChartDatum[]
  title?: string
  /** Shown in place of the chart when there's nothing to plot. */
  emptyMessage?: string
  className?: string
}

// KTD6 — no charting library is installed in this repo (confirmed against
// package.json); this is a small, dependency-free inline-SVG bar chart for
// the reports page (U7). Each row is its own `<svg>` with a single `<rect>`
// scaled 0-100 against the largest value, so bar width is pure CSS/SVG, not
// a canvas draw — degrades to an empty state when a cycle has no data (e.g.
// a freshly-created cycle), rather than a chart of zero-width or missing
// bars (R10, R11).
export function BarChart({ data, title, emptyMessage = 'No data for this cycle yet.', className }: BarChartProps) {
  const total = data.reduce((sum, datum) => sum + datum.value, 0)
  const isEmpty = data.length === 0 || total === 0
  const max = Math.max(1, ...data.map((datum) => datum.value))

  return (
    <div className={cx('bar-chart', className)}>
      {title != null && <h3 className="bar-chart__title">{title}</h3>}
      {isEmpty ? (
        <p className="text-small text-ink-muted">{emptyMessage}</p>
      ) : (
        <ul className="bar-chart__rows">
          {data.map((datum) => {
            const widthPct = Math.round((datum.value / max) * 100)
            return (
              <li key={datum.key} className="bar-chart__row">
                <span className="bar-chart__label">{datum.label}</span>
                <svg
                  className="bar-chart__bar"
                  viewBox="0 0 100 10"
                  preserveAspectRatio="none"
                  role="img"
                  aria-label={`${datum.label}: ${datum.value}`}
                >
                  <rect
                    x="0"
                    y="0"
                    width={widthPct}
                    height="10"
                    rx="2"
                    className="bar-chart__rect"
                    style={datum.color != null ? ({ '--bar-color': datum.color } as CSSProperties) : undefined}
                  />
                </svg>
                <span className="bar-chart__value">{datum.value}</span>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
